/**
 * `/pricing-config` — read-only viewer (Phase 3).
 *
 * Renders the active pricing config grouped by service. Admins (Cognito
 * group `pricing-admin`) see every field; staff get a stripped view with
 * an "INTERNAL fields hidden" disclosure listing the omitted paths so
 * they know what they aren't seeing.
 *
 * Phase 4 will add the editor on top of this surface.
 */

import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';

type ViewResponse = components['schemas']['PricingConfigViewResponse'];
type ViewBody = ViewResponse['body'];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: ViewResponse }
  | { kind: 'error'; message: string };

const SOURCE_LABEL: Record<NonNullable<ViewResponse['meta']['source']>, string> = {
  cache: 'cached',
  ddb: 'live',
  'fallback-unseeded': 'fallback (not yet seeded)',
  'fallback-error': 'fallback (DDB unreachable)',
};

export function PricingConfigPage() {
  const client = useApiClient();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await client.GET('/pricing-config/current', {});
        if (cancelled) return;
        if (error) {
          setState({
            kind: 'error',
            message: error?.error?.detail ?? 'Failed to load pricing config.',
          });
          return;
        }
        if (!data?.ok) {
          setState({ kind: 'error', message: 'Empty response from server.' });
          return;
        }
        // openapi-typescript v7 widens tuple types (e.g. digiCosts) to
        // arrays in the openapi-fetch response generic. The named
        // `PricingConfigViewResponse` schema preserves the tuples, so the
        // structural shape we actually receive is a strict subset of our
        // rendered alias — cast and let the runtime shape govern.
        setState({ kind: 'ready', data: data.data as unknown as ViewResponse });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Network error.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Pricing config</h1>
        <p className="text-sm text-muted-foreground">
          Active configuration used by every quote endpoint and the calculator.
        </p>
      </header>

      {state.kind === 'loading' && <LoadingSkeleton />}

      {state.kind === 'error' && (
        <Alert variant="destructive">
          <AlertTitle>Could not load pricing config</AlertTitle>
          <AlertDescription>{state.message}</AlertDescription>
        </Alert>
      )}

      {state.kind === 'ready' && <ConfigView data={state.data} />}
    </div>
  );
}

function ConfigView({ data }: { data: ViewResponse }) {
  const { meta, body, isAdmin, internalFieldsHidden } = data;
  const isFallback = meta.source.startsWith('fallback');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="space-y-0.5">
            <CardTitle className="text-base">
              {meta.version === null ? 'Bundled defaults' : `Version ${meta.version}`}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Source: {SOURCE_LABEL[meta.source]} · fetched{' '}
              {new Date(meta.fetchedAt).toLocaleString()}
            </p>
          </div>
          <Badge variant={isAdmin ? 'default' : 'secondary'}>
            {isAdmin ? 'Admin view' : 'Staff view'}
          </Badge>
        </CardHeader>
        {meta.publishedBy && (
          <CardContent className="text-xs text-muted-foreground">
            Published {meta.publishedAt ? new Date(meta.publishedAt).toLocaleString() : '—'} by{' '}
            <span className="font-mono">{meta.publishedBy.email}</span>
            {meta.notes && (
              <>
                <Separator className="my-2" />
                <span>{meta.notes}</span>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {isFallback && (
        <Alert>
          <AlertTitle>Showing fallback config</AlertTitle>
          <AlertDescription>
            {meta.source === 'fallback-unseeded'
              ? 'No pricing config has been published to DynamoDB yet — quotes are using the bundled defaults. Run the seed CLI once to migrate.'
              : 'Pricing config could not be read from DynamoDB. Quotes are using the last cached values or the bundled defaults until the next refresh.'}
          </AlertDescription>
        </Alert>
      )}

      {!isAdmin && internalFieldsHidden.length > 0 && (
        <Alert>
          <AlertTitle>Internal fields hidden</AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              {internalFieldsHidden.length} field
              {internalFieldsHidden.length === 1 ? '' : 's'} hidden from your role:
            </p>
            <ul className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs font-mono sm:grid-cols-2">
              {internalFieldsHidden.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <ScreenPrintCard sp={body.screenPrint} isAdmin={isAdmin} />
      <DtfTransferCard dtf={body.dtfTransfer} isAdmin={isAdmin} />
      <DtfPressAndPressCard pp={body.dtfPressAndPress} isAdmin={isAdmin} />
      <DtgCard dtg={body.dtg} isAdmin={isAdmin} />
      <EmbroideryCard emb={body.embroidery} isAdmin={isAdmin} />
      <UniversalCard u={body.universal} />
      <RoutingCard r={body.routing} />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function ScreenPrintCard({ sp, isAdmin }: { sp: ViewBody['screenPrint']; isAdmin: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Screen Print</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <KeyValue label="Minimum qty" value={`${sp.minQty} pieces`} />
        <DarkUnderbase d={sp.darkUnderbase} />
        <RushMultipliers r={sp.rushMultipliers} />
        <Separator />
        <SectionHeading>Customer-facing rates</SectionHeading>
        <TierTable
          title="Retail (B2C) — White"
          rows={sp.retailPrintTiers.white}
          valueKey="rate"
          valueLabel="$/piece"
        />
        <TierTable
          title="Retail (B2C) — Colored"
          rows={sp.retailPrintTiers.colored}
          valueKey="rate"
          valueLabel="$/piece"
        />
        <TierTable
          title="Wholesale (B2B)"
          rows={sp.wholesalePrintTiers}
          valueKey="rate"
          valueLabel="$/piece"
        />
        <TierTable
          title="Setup fee (B2C)"
          rows={sp.setupTiers}
          valueKey="setup"
          valueLabel="$ flat"
        />
        <TierTable
          title="Extra color upcharge"
          rows={sp.extraColorUpcharge}
          valueKey="upcharge"
          valueLabel="$/piece"
        />
        <TierTable
          title="Extra location upcharge"
          rows={sp.locationUpcharge}
          valueKey="upcharge"
          valueLabel="$/piece"
        />
        {isAdmin && sp.cpcTiers && (
          <>
            <Separator />
            <SectionHeading>
              Internal <InternalBadge />
            </SectionHeading>
            <TierTable
              title="Cost per color (CPC)"
              rows={sp.cpcTiers}
              valueKey="cpc"
              valueLabel="$/piece"
            />
            {sp.floorTiers && (
              <TierTable
                title="Negotiation floor"
                rows={sp.floorTiers}
                valueKey="floor"
                valueLabel="$/piece"
              />
            )}
            {sp.internalLocationCost !== undefined && (
              <KeyValue
                label="Internal location cost"
                value={`$${sp.internalLocationCost.toFixed(2)}`}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DtfTransferCard({ dtf, isAdmin }: { dtf: ViewBody['dtfTransfer']; isAdmin: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">DTF Transfer</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <KeyValue label="Sheet width" value={`${dtf.sheetWidthIn}"`} />
        <SectionHeading>Base rate by length (B2C)</SectionHeading>
        <DtfBaseRateTable rows={dtf.baseRateTiers} />
        {isAdmin && (dtf.b2bMultiplier !== undefined || dtf.floor) && (
          <>
            <Separator />
            <SectionHeading>
              Internal <InternalBadge />
            </SectionHeading>
            <div className="grid gap-2 sm:grid-cols-2">
              {dtf.internalCostPerSqIn !== undefined && (
                <KeyValue
                  label="Internal cost per sq-in"
                  value={`$${dtf.internalCostPerSqIn.toFixed(4)}`}
                />
              )}
              {dtf.b2bMultiplier !== undefined && (
                <KeyValue label="B2B multiplier" value={dtf.b2bMultiplier.toFixed(2)} />
              )}
              {dtf.b2bMinRate !== undefined && (
                <KeyValue label="B2B min rate" value={`$${dtf.b2bMinRate.toFixed(4)}`} />
              )}
              {dtf.etsyDefaultRate !== undefined && (
                <KeyValue label="Etsy default rate" value={`$${dtf.etsyDefaultRate.toFixed(4)}`} />
              )}
              {dtf.etsyMultiplier !== undefined && (
                <KeyValue label="Etsy multiplier" value={dtf.etsyMultiplier.toFixed(2)} />
              )}
            </div>
            {dtf.floor && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <KeyValue label="Floor B2C" value={`$${dtf.floor.b2c.toFixed(2)}`} />
                <KeyValue label="Floor B2B" value={`$${dtf.floor.b2b.toFixed(2)}`} />
                <KeyValue label="Floor Etsy" value={`$${dtf.floor.etsy.toFixed(2)}`} />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DtfPressAndPressCard({
  pp,
  isAdmin,
}: {
  pp: ViewBody['dtfPressAndPress'];
  isAdmin: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">DTF Press & Press</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <KeyValue
          label="Default labor / location"
          value={`$${pp.laborPerLocationDefault.toFixed(2)}`}
        />
        {isAdmin && pp.marginTiers && (
          <>
            <Separator />
            <SectionHeading>
              Internal <InternalBadge />
            </SectionHeading>
            {pp.transferCostPerSqIn !== undefined && (
              <KeyValue
                label="Transfer cost / sq-in"
                value={`$${pp.transferCostPerSqIn.toFixed(4)}`}
              />
            )}
            {pp.minSellBeforeLaborBlank !== undefined && (
              <KeyValue
                label="Min sell above labor+blank"
                value={`$${pp.minSellBeforeLaborBlank.toFixed(2)}`}
              />
            )}
            <TierTable
              title="Auto margin by qty"
              rows={pp.marginTiers}
              valueKey="marginPct"
              valueLabel="%"
            />
            {pp.marginWarnFloorPct !== undefined && (
              <KeyValue label="Margin warn floor" value={`${pp.marginWarnFloorPct}%`} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DtgCard({ dtg, isAdmin }: { dtg: ViewBody['dtg']; isAdmin: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">DTG</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <KeyValue label="Hard max qty" value={`${dtg.hardMaxQty - 1} pieces (cap)`} />
          <KeyValue label="Soft max qty" value={`${dtg.softMaxQty - 1} pieces`} />
        </div>
        <SectionHeading>Customer price by qty (top of tier)</SectionHeading>
        <DtgPriceTable rows={dtg.priceTiers} />
        {isAdmin && dtg.internalCost && (
          <>
            <Separator />
            <SectionHeading>
              Internal <InternalBadge />
            </SectionHeading>
            <div className="grid gap-2 sm:grid-cols-2">
              <KeyValue
                label="Internal cost (light)"
                value={`$${dtg.internalCost.light.toFixed(2)}`}
              />
              <KeyValue
                label="Internal cost (dark)"
                value={`$${dtg.internalCost.dark.toFixed(2)}`}
              />
            </div>
            {dtg.marginWarnFloorPct !== undefined && (
              <KeyValue label="Margin warn floor" value={`${dtg.marginWarnFloorPct}%`} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EmbroideryCard({ emb, isAdmin }: { emb: ViewBody['embroidery']; isAdmin: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Embroidery</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <KeyValue label="Minimum qty" value={`${emb.minQty} pieces`} />
        <SectionHeading>Base rate by placement</SectionHeading>
        <TierTable
          title="Left chest"
          rows={emb.baseTiers.lc}
          valueKey="rate"
          valueLabel="$/piece"
        />
        <TierTable title="Hat" rows={emb.baseTiers.hat} valueKey="rate" valueLabel="$/piece" />
        <TierTable title="Back" rows={emb.baseTiers.back} valueKey="rate" valueLabel="$/piece" />
        <TierTable
          title="Additional location"
          rows={emb.additionalLocationTiers}
          valueKey="rate"
          valueLabel="$/piece"
        />
        <TierTable
          title="Name (personalization)"
          rows={emb.nameTiers}
          valueKey="rate"
          valueLabel="$/piece"
        />
        <KeyValue label="Number multiplier" value={emb.numberMultiplier.toFixed(2)} />
        <SectionHeading>Complexity multiplier</SectionHeading>
        <div className="grid gap-2 sm:grid-cols-3">
          <KeyValue label="Simple" value={emb.complexityMultiplier.simple.toFixed(2)} />
          <KeyValue label="Standard" value={emb.complexityMultiplier.standard.toFixed(2)} />
          <KeyValue label="Complex" value={emb.complexityMultiplier.complex.toFixed(2)} />
        </div>
        <KeyValue label="Customer digi markup" value={`$${emb.digiCustomerMarkup.toFixed(2)}`} />
        {isAdmin && emb.digiCosts && (
          <>
            <Separator />
            <SectionHeading>
              Internal <InternalBadge />
            </SectionHeading>
            <div className="grid gap-1 text-sm sm:grid-cols-2">
              {Object.entries(emb.digiCosts).map(([type, [normal, rush]]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="font-mono text-xs">{type}</span>
                  <span className="text-xs text-muted-foreground">
                    ${normal.toFixed(2)} / ${rush.toFixed(2)} rush
                  </span>
                </div>
              ))}
            </div>
            {emb.costDefaults && (
              <div className="grid gap-2 sm:grid-cols-3">
                <KeyValue
                  label="Cost / 1k stitches"
                  value={`$${emb.costDefaults.costPerThousandStitches.toFixed(2)}`}
                />
                <KeyValue
                  label="Overhead / piece"
                  value={`$${emb.costDefaults.overheadPerPiece.toFixed(2)}`}
                />
                <KeyValue
                  label="Thread / piece"
                  value={`$${emb.costDefaults.threadPerPiece.toFixed(2)}`}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function UniversalCard({ u }: { u: ViewBody['universal'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Universal — Size upcharges</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 sm:grid-cols-2">
          <KeyValue label="2XL" value={`+$${u.sizeUpcharge.xxl.toFixed(2)}/piece`} />
          <KeyValue label="3XL" value={`+$${u.sizeUpcharge.xxxl.toFixed(2)}/piece`} />
        </div>
      </CardContent>
    </Card>
  );
}

function RoutingCard({ r }: { r: ViewBody['routing'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Routing thresholds</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 sm:grid-cols-2">
          <KeyValue label="DTG max qty" value={r.thresholds.dtgMaxQty} />
          <KeyValue label="Min qty for SP" value={r.thresholds.dtfPpMinForSp} />
          <KeyValue
            label="SP single-side clear color cap"
            value={r.thresholds.spSingleSideMaxColorsClear}
          />
          <KeyValue label="Double-sided volume break" value={r.thresholds.doubleSidedVolumeBreak} />
          <KeyValue label="Any-service volume break" value={r.thresholds.anyServiceVolumeBreak} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Small composables ──────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">{children}</h3>
  );
}

function InternalBadge() {
  return (
    <Badge variant="secondary" className="text-xs">
      INTERNAL
    </Badge>
  );
}

function KeyValue({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

type SimpleTierRow = { minQty: number } & Record<string, number>;

function TierTable({
  title,
  rows,
  valueKey,
  valueLabel,
}: {
  title: string;
  rows: ReadonlyArray<SimpleTierRow>;
  valueKey: string;
  valueLabel: string;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                Min qty
              </th>
              <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                {valueLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${title}-${row.minQty}`}
                className="border-t [&:nth-child(even)]:bg-muted/20"
              >
                <td className="px-3 py-1.5 font-mono text-xs">{row.minQty.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs">
                  {formatTierValue(row[valueKey])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatTierValue(n: number | undefined): string {
  if (n === undefined) return '—';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(n < 1 ? 4 : 2);
}

function DarkUnderbase({ d }: { d: ViewBody['screenPrint']['darkUnderbase'] }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">Dark underbase (colored only)</p>
      <div className="grid gap-2 sm:grid-cols-3">
        <KeyValue label="Qty break" value={`${d.qtyBreak}+`} />
        <KeyValue label="Below break" value={`+$${d.below.toFixed(2)}`} />
        <KeyValue label="At/above break" value={`+$${d.atOrAbove.toFixed(2)}`} />
      </div>
    </div>
  );
}

function RushMultipliers({ r }: { r: ViewBody['screenPrint']['rushMultipliers'] }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">Rush multipliers</p>
      <div className="grid gap-2 sm:grid-cols-3">
        <KeyValue label="None" value={`×${r.none.toFixed(2)}`} />
        <KeyValue label="Rush" value={`×${r.rush.toFixed(2)}`} />
        <KeyValue label="Hot rush" value={`×${r.hot_rush.toFixed(2)}`} />
      </div>
    </div>
  );
}

function DtfBaseRateTable({ rows }: { rows: ViewBody['dtfTransfer']['baseRateTiers'] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
              Up to length
            </th>
            <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
              $/sq-in
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const lengthLabel =
              r.maxLength >= Number.MAX_SAFE_INTEGER ? 'unbounded' : `${r.maxLength}"`;
            return (
              <tr
                key={`dtf-base-${r.maxLength}`}
                className="border-t [&:nth-child(even)]:bg-muted/20"
              >
                <td className="px-3 py-1.5 font-mono text-xs">{lengthLabel}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs">{r.rate.toFixed(4)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DtgPriceTable({ rows }: { rows: ViewBody['dtg']['priceTiers'] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
              Up to qty
            </th>
            <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
              Light · single
            </th>
            <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
              Light · double
            </th>
            <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
              Dark · single
            </th>
            <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
              Dark · double
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`dtg-${r.maxQty}`} className="border-t [&:nth-child(even)]:bg-muted/20">
              <td className="px-3 py-1.5 font-mono text-xs">{r.maxQty}</td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">
                ${r.light.single.toFixed(2)}
              </td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">
                ${r.light.double.toFixed(2)}
              </td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">
                ${r.dark.single.toFixed(2)}
              </td>
              <td className="px-3 py-1.5 text-right font-mono text-xs">
                ${r.dark.double.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
